import api from './api';

export interface ProjectUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  role?: string;
}

export interface ProjectColumn {
  id: string;
  title: string;
  color: string;
  order: number;
}

export interface ProjectFile {
  _id: string;
  originalName: string;
  storedFilename: string;
  mimeType: string;
  size: number;
  uploadedBy?: ProjectUser;
  createdAt: string;
}

export interface ProjectComment {
  _id: string;
  text: string;
  createdBy?: ProjectUser;
  createdAt: string;
  updatedAt?: string | null;
  versions?: Array<{ text: string; editedAt: string; editedBy?: ProjectUser }>;
  attachments?: ProjectFile[];
}

export interface ProjectTask {
  _id: string;
  title: string;
  description?: string;
  status: string;
  priority: 'low' | 'medium' | 'high';
  project: string;
  assignedTo?: ProjectUser | null;
  participants: ProjectUser[];
  createdBy?: ProjectUser;
  lastUpdatedBy?: ProjectUser | null;
  dueDate?: string | null;
  order: number;
  completionPercent: number;
  dependsOn: Array<{ _id: string; title: string; status: string } | string>;
  comments: ProjectComment[];
  attachments: ProjectFile[];
  createdAt: string;
  updatedAt: string;
  hasUnreadActivity?: boolean;
}

export interface ProjectCompany {
  _id: string;
  name: string;
  isOwner?: boolean;
  modules?: {
    projects?: boolean;
    projectsHidden?: boolean;
    projectsTrialStartedAt?: string;
    projectsTrialEndsAt?: string;
  };
}

export interface Project {
  _id: string;
  name: string;
  description?: string;
  company: ProjectCompany | string;
  createdBy?: ProjectUser;
  members: ProjectUser[];
  columns: ProjectColumn[];
  startDate?: string | null;
  endDate?: string | null;
  dateChangeHistory?: any[];
  status: 'active' | 'archived';
  finishedColumnId?: string | null;
  taskCompletion?: { total: number; completed: number; percent: number };
  hasUnreadActivity?: boolean;
  createdAt: string;
  updatedAt: string;
}

const unwrap = <T,>(response: any): T => response.data.data ?? response.data;

const downloadBlob = async (url: string, filename: string) => {
  const response = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
};

export const projectService = {
  async listUnlockedCompanies() {
    const response = await api.get('/projects/companies');
    return unwrap<ProjectCompany[]>(response);
  },

  async startDemo() {
    const response = await api.post('/projects/demo');
    return unwrap<{ company: ProjectCompany; startedAt: string; endsAt: string }>(response);
  },

  async hideModule() {
    const response = await api.post('/projects/hide');
    return unwrap<{ company: ProjectCompany }>(response);
  },

  async listProjects(companyId?: string, includeArchived = false) {
    const response = await api.get('/projects', {
      params: { companyId, includeArchived: includeArchived ? 'true' : undefined }
    });
    return unwrap<Project[]>(response);
  },

  async getProject(id: string) {
    const response = await api.get(`/projects/${id}`);
    return unwrap<Project>(response);
  },

  async createProject(data: {
    name: string;
    description?: string;
    company?: string;
    members?: string[];
    startDate?: string | null;
    endDate?: string | null;
  }) {
    const response = await api.post('/projects', data);
    return unwrap<Project>(response);
  },

  async listCompanyUsers(companyId?: string) {
    const response = await api.get('/projects/users', {
      params: companyId ? { companyId } : undefined
    });
    return unwrap<ProjectUser[]>(response);
  },

  async updateProject(id: string, data: Partial<Project>) {
    const response = await api.put(`/projects/${id}`, data);
    return unwrap<Project>(response);
  },

  async updateDates(id: string, data: { startDate?: string | null; endDate?: string | null; reason: string }) {
    const response = await api.patch(`/projects/${id}/dates`, data);
    return unwrap<Project>(response);
  },

  async listUsers(projectId: string) {
    const response = await api.get(`/projects/${projectId}/users`);
    return unwrap<ProjectUser[]>(response);
  },

  async addMember(projectId: string, userId: string) {
    const response = await api.post(`/projects/${projectId}/members`, { userId });
    return unwrap<Project>(response);
  },

  async removeMember(projectId: string, userId: string) {
    const response = await api.delete(`/projects/${projectId}/members/${userId}`);
    return unwrap<Project>(response);
  },

  async setFinishedColumn(projectId: string, columnId: string | null) {
    const response = await api.patch(`/projects/${projectId}/finished-column`, { columnId });
    return unwrap<Project>(response);
  },

  async addColumn(projectId: string, data: { title: string; color?: string; setAsFinished?: boolean }) {
    const response = await api.post(`/projects/${projectId}/columns`, data);
    return unwrap<ProjectColumn>(response);
  },

  async reorderColumns(projectId: string, columnIds: string[]) {
    const response = await api.patch(`/projects/${projectId}/columns/reorder`, { columnIds });
    return unwrap<Project>(response);
  },

  async deleteColumn(projectId: string, columnId: string) {
    await api.delete(`/projects/${projectId}/columns/${columnId}`);
  },

  async archiveProject(projectId: string, archived: boolean) {
    const response = await api.patch(`/projects/${projectId}/archive`, { archived });
    return unwrap<Project>(response);
  },

  async deleteProject(projectId: string) {
    await api.delete(`/projects/${projectId}`);
  },

  async acknowledge(projectId: string) {
    const response = await api.put(`/projects/${projectId}/acknowledge`);
    return unwrap(response);
  },

  async listTasks(projectId: string) {
    const response = await api.get(`/projects/${projectId}/tasks`);
    return unwrap<ProjectTask[]>(response);
  },

  async createTask(projectId: string, data: Partial<ProjectTask> & { participants?: string[]; dependsOn?: string[] }) {
    const response = await api.post(`/projects/${projectId}/tasks`, data);
    return unwrap<ProjectTask>(response);
  },

  async updateTask(taskId: string, data: any) {
    const response = await api.put(`/projects/tasks/${taskId}`, data);
    return unwrap<ProjectTask>(response);
  },

  async updateTaskStatus(taskId: string, status: string, order?: number) {
    const response = await api.patch(`/projects/tasks/${taskId}/status`, { status, order });
    return unwrap<ProjectTask>(response);
  },

  async deleteTask(taskId: string) {
    await api.delete(`/projects/tasks/${taskId}`);
  },

  async markTaskSeen(taskId: string) {
    const response = await api.put(`/projects/tasks/${taskId}/seen`);
    return unwrap(response);
  },

  async addComment(taskId: string, text: string, file?: File) {
    if (file) {
      const form = new FormData();
      form.append('text', text);
      form.append('file', file);
      const response = await api.post(`/projects/tasks/${taskId}/comments`, form);
      return unwrap<ProjectTask>(response);
    }
    const response = await api.post(`/projects/tasks/${taskId}/comments`, { text });
    return unwrap<ProjectTask>(response);
  },

  async updateComment(taskId: string, commentId: string, text: string) {
    const response = await api.put(`/projects/tasks/${taskId}/comments/${commentId}`, { text });
    return unwrap<ProjectTask>(response);
  },

  async deleteComment(taskId: string, commentId: string) {
    const response = await api.delete(`/projects/tasks/${taskId}/comments/${commentId}`);
    return unwrap<ProjectTask>(response);
  },

  async uploadTaskFile(taskId: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/projects/tasks/${taskId}/attachments`, form);
    return unwrap<ProjectTask>(response);
  },

  async deleteTaskFile(taskId: string, attachmentId: string) {
    const response = await api.delete(`/projects/tasks/${taskId}/attachments/${attachmentId}`);
    return unwrap<ProjectTask>(response);
  },

  async downloadTaskFile(taskId: string, attachmentId: string, filename: string) {
    await downloadBlob(`/projects/tasks/${taskId}/attachments/${attachmentId}/file`, filename);
  },

  async downloadCommentFile(taskId: string, commentId: string, attachmentId: string, filename: string) {
    await downloadBlob(
      `/projects/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}/file`,
      filename
    );
  },

  async uploadCommentFile(taskId: string, commentId: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/projects/tasks/${taskId}/comments/${commentId}/attachments`, form);
    return unwrap<ProjectTask>(response);
  },

  async deleteCommentFile(taskId: string, commentId: string, attachmentId: string) {
    const response = await api.delete(
      `/projects/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}`
    );
    return unwrap<ProjectTask>(response);
  }
};

export const personName = (user?: ProjectUser | null): string => {
  if (!user) return 'Unknown';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
};
